/**
 * Support Store — customer support chat state.
 *
 * Uses Immer middleware for immutable updates with mutable syntax.
 * Manages conversations, messages, unread tracking, and socket events.
 *
 * Key design decisions:
 *   - unreadCount is DERIVED from unreadConversations map (single source of truth)
 *   - startConversation returns a Promise<string> (conversation ID) so callers can await it
 *   - No subscribe hacks — async/await flow for create-then-send
 */

import "./immerSetup";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { EVENTS } from "@bash/shared";
import type {
  SupportConversation,
  SupportMessage,
  SupportArticle,
  SupportAttachment,
  SupportRecentTask,
  SupportMessageReceivedPayload,
  SupportConversationUpdatePayload,
  SupportMessagesReadPayload,
} from "@bash/shared";
import { getSocket } from "./socketService";
import { posthog } from "@/lib/posthog";
import { ANALYTICS_EVENTS } from "@bash/shared";

// ─── Types ──────────────────────────────────────────────────────────────────

interface SupportStoreState {
  conversations: SupportConversation[];
  activeConversationId: string | null;
  messages: SupportMessage[];
  agentTyping: boolean;
  unreadConversations: Map<string, number>; // conversationId → unread count
  searchQuery: string;
  searchResults: SupportArticle[];
  isLoading: boolean;
  lastMessagePreviews: Record<string, string>;
  /** "Link a task" picker contents. */
  recentTasks: SupportRecentTask[];
  /** conversationId → the task the ticket is about (from the open callback). */
  linkedTasks: Record<string, SupportRecentTask | null>;
}

interface SupportStoreActions {
  // Returns the new conversation ID (or null on failure)
  startConversation: (
    pageUrl?: string,
    taskId?: string
  ) => Promise<string | null>;
  sendMessage: (content: string, attachments?: SupportAttachment[]) => void;
  loadConversations: () => void;
  loadRecentTasks: () => void;
  openConversation: (id: string) => void;
  searchArticles: (query: string) => void;
  resolveConversation: () => void;
  rateConversation: (rating: number) => void;
  linkTask: (taskId: string) => void;
  // Derived
  getUnreadCount: () => number;
  // Socket handlers
  handleMessageReceived: (payload: SupportMessageReceivedPayload) => void;
  handleConversationUpdate: (payload: SupportConversationUpdatePayload) => void;
  handleAgentTyping: (typing: boolean) => void;
  handleMessagesRead: (payload: SupportMessagesReadPayload) => void;
}

type SupportStore = SupportStoreState & SupportStoreActions;

// ─── Store ──────────────────────────────────────────────────────────────────

export const useSupportStore = create<SupportStore>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      conversations: [],
      activeConversationId: null,
      messages: [],
      agentTyping: false,
      unreadConversations: new Map<string, number>(),
      searchQuery: "",
      searchResults: [],
      isLoading: false,
      lastMessagePreviews: {},
      recentTasks: [],
      linkedTasks: {},

      // =========================================================================
      // Derived
      // =========================================================================

      getUnreadCount: () => {
        let total = 0;
        for (const count of get().unreadConversations.values()) {
          total += count;
        }
        return total;
      },

      // =========================================================================
      // Actions
      // =========================================================================

      startConversation: async (
        pageUrl?: string,
        taskId?: string
      ): Promise<string | null> => {
        const socket = getSocket();
        if (!socket) return null;

        posthog.capture(ANALYTICS_EVENTS.SUPPORT_CONVERSATION_OPENED, {
          source: "widget",
        });

        set((state) => {
          state.isLoading = true;
          state.messages = [];
          state.activeConversationId = null;
        });

        return new Promise<string | null>((resolve) => {
          const payload: { page_url?: string; task_id?: string } = {};
          if (pageUrl) payload.page_url = pageUrl;
          if (taskId) payload.task_id = taskId;

          socket.emit(
            EVENTS.SUPPORT_CONVERSATION_OPEN,
            payload,
            (response: {
              error?: string;
              conversation?: SupportConversation;
              messages?: SupportMessage[];
              linked_task?: SupportRecentTask | null;
            }) => {
              if (response.error) {
                console.warn(
                  "[SupportStore] Failed to open conversation:",
                  response.error
                );
                set((state) => {
                  state.isLoading = false;
                });
                resolve(null);
                return;
              }

              set((state) => {
                state.isLoading = false;
                if (response.conversation) {
                  state.activeConversationId = response.conversation.id;
                  state.linkedTasks[response.conversation.id] =
                    response.linked_task ?? null;
                  const exists = state.conversations.some(
                    (c) => c.id === response.conversation!.id
                  );
                  if (!exists) {
                    state.conversations.unshift(response.conversation);
                  }
                }
                if (response.messages) {
                  state.messages = response.messages;
                }
              });

              resolve(response.conversation?.id ?? null);
            }
          );
        });
      },

      sendMessage: (content: string, attachments?: SupportAttachment[]) => {
        const { activeConversationId } = get();
        const socket = getSocket();
        if (!socket || !activeConversationId) return;

        const optimisticId = `optimistic-${Date.now()}`;
        const optimisticMsg: SupportMessage = {
          id: optimisticId,
          conversation_id: activeConversationId,
          sender_type: "user",
          sender_id: null,
          content,
          is_internal_note: false,
          // Optimistic attachments keep whatever preview URL the composer set;
          // the server echo replaces them with storage-signed ones.
          attachments: attachments ?? [],
          metadata: { _optimistic: true },
          read_at: null,
          sender_name: null,
          sender_avatar_url: null,
          created_at: new Date().toISOString(),
        };

        set((state) => {
          state.messages.push(optimisticMsg);
        });

        socket.emit(
          EVENTS.SUPPORT_MESSAGE_SEND,
          {
            conversation_id: activeConversationId,
            content,
            attachments: (attachments ?? []).map(
              ({ signed_url: _preview, ...att }) => att
            ),
          },
          (response: { error?: string }) => {
            if (response.error) {
              console.warn(
                "[SupportStore] Failed to send message:",
                response.error
              );
              set((state) => {
                state.messages = state.messages.filter(
                  (m) => m.id !== optimisticId
                );
              });
            }
          }
        );
      },

      loadRecentTasks: () => {
        const socket = getSocket();
        if (!socket) return;

        socket.emit(
          EVENTS.SUPPORT_RECENT_TASKS,
          (response: { error?: string; tasks?: SupportRecentTask[] }) => {
            if (response.error || !response.tasks) return;
            set((state) => {
              state.recentTasks = response.tasks!;
            });
          }
        );
      },

      linkTask: (taskId: string) => {
        const { activeConversationId } = get();
        const socket = getSocket();
        if (!socket || !activeConversationId) return;

        socket.emit(
          EVENTS.SUPPORT_LINK_TASK,
          {
            conversation_id: activeConversationId,
            task_id: taskId,
          },
          (response: {
            error?: string;
            conversation?: SupportConversation;
            linked_task?: SupportRecentTask;
          }) => {
            if (response.error || !response.conversation) return;
            set((state) => {
              state.linkedTasks[response.conversation!.id] =
                response.linked_task ?? null;
              const idx = state.conversations.findIndex(
                (c) => c.id === response.conversation!.id
              );
              if (idx !== -1) {
                state.conversations[idx] = response.conversation!;
              }
            });
          }
        );
      },

      rateConversation: (rating: number) => {
        const { activeConversationId } = get();
        const socket = getSocket();
        if (!socket || !activeConversationId) return;

        socket.emit(
          EVENTS.SUPPORT_RATE,
          {
            conversation_id: activeConversationId,
            rating,
          },
          (response: {
            error?: string;
            conversation?: SupportConversation;
          }) => {
            if (response.error || !response.conversation) return;
            set((state) => {
              const idx = state.conversations.findIndex(
                (c) => c.id === response.conversation!.id
              );
              if (idx !== -1) {
                state.conversations[idx] = response.conversation!;
              }
            });
          }
        );
      },

      loadConversations: () => {
        const socket = getSocket();
        if (!socket) return;

        socket.emit(
          EVENTS.SUPPORT_CONVERSATION_LIST,
          (response: {
            error?: string;
            conversations?: SupportConversation[];
          }) => {
            if (response.error) {
              console.warn(
                "[SupportStore] Failed to list conversations:",
                response.error
              );
              return;
            }

            set((state) => {
              if (response.conversations) {
                state.conversations = response.conversations;
              }
            });
          }
        );
      },

      openConversation: (id: string) => {
        const socket = getSocket();
        if (!socket) return;

        set((state) => {
          state.isLoading = true;
          state.messages = [];
          state.activeConversationId = id;
          // Clear unread for this conversation
          state.unreadConversations.delete(id);
        });

        socket.emit(
          EVENTS.SUPPORT_CONVERSATION_OPEN,
          { conversation_id: id },
          (response: {
            error?: string;
            conversation?: SupportConversation;
            messages?: SupportMessage[];
            linked_task?: SupportRecentTask | null;
          }) => {
            if (response.error) {
              console.warn(
                "[SupportStore] Failed to open conversation:",
                response.error
              );
              set((state) => {
                state.isLoading = false;
              });
              return;
            }

            set((state) => {
              state.isLoading = false;
              if (response.conversation) {
                state.activeConversationId = response.conversation.id;
                state.linkedTasks[response.conversation.id] =
                  response.linked_task ?? null;
                const idx = state.conversations.findIndex(
                  (c) => c.id === response.conversation!.id
                );
                if (idx !== -1) {
                  state.conversations[idx] = response.conversation;
                } else {
                  state.conversations.unshift(response.conversation);
                }
              }
              if (response.messages) {
                state.messages = response.messages;
              }
            });
          }
        );
      },

      resolveConversation: () => {
        const { activeConversationId } = get();
        const socket = getSocket();
        if (!socket || !activeConversationId) return;

        socket.emit(
          EVENTS.SUPPORT_CUSTOMER_RESOLVE,
          { conversation_id: activeConversationId },
          (response: { error?: string }) => {
            if (!response.error) {
              set((state) => {
                const idx = state.conversations.findIndex(
                  (c) => c.id === activeConversationId
                );
                if (idx !== -1) {
                  state.conversations[idx].status = "resolved";
                  state.conversations[idx].resolved_at =
                    new Date().toISOString();
                  state.conversations[idx].resolved_by = "user";
                }
              });
            }
          }
        );
      },

      searchArticles: (query: string) => {
        const socket = getSocket();
        if (!socket) return;

        set((state) => {
          state.searchQuery = query;
        });

        if (!query.trim()) {
          set((state) => {
            state.searchResults = [];
          });
          return;
        }

        socket.emit(
          EVENTS.SUPPORT_ARTICLE_SEARCH,
          { query },
          (response: { error?: string; articles?: SupportArticle[] }) => {
            if (response.error) return;
            set((state) => {
              if (response.articles) state.searchResults = response.articles;
            });
          }
        );
      },

      // ─── Socket Handlers ────────────────────────────────────────────────────

      handleMessageReceived: (payload: SupportMessageReceivedPayload) => {
        set((state) => {
          const { message, conversation } = payload;

          // Add message to active conversation (with optimistic dedup)
          if (message.conversation_id === state.activeConversationId) {
            const optimisticIdx = state.messages.findIndex(
              (m) =>
                m.metadata?._optimistic &&
                m.content === message.content &&
                m.sender_type === "user"
            );
            if (optimisticIdx !== -1) {
              state.messages[optimisticIdx] = message;
            } else {
              const exists = state.messages.some((m) => m.id === message.id);
              if (!exists) {
                state.messages.push(message);
              }
            }
            state.agentTyping = false;
          }

          // Update last message preview
          const preview =
            message.content.length > 60
              ? message.content.slice(0, 60) + "..."
              : message.content;
          state.lastMessagePreviews[message.conversation_id] = preview;

          // Update conversation in list (the server sends null when its read-back failed)
          if (conversation) {
            const idx = state.conversations.findIndex(
              (c) => c.id === conversation.id
            );
            if (idx !== -1) {
              state.conversations[idx] = conversation;
            } else {
              state.conversations.unshift(conversation);
            }
          }

          // Increment unread if not viewing this conversation
          if (message.conversation_id !== state.activeConversationId) {
            const prev =
              state.unreadConversations.get(message.conversation_id) || 0;
            state.unreadConversations.set(message.conversation_id, prev + 1);
          }
        });
      },

      handleConversationUpdate: (payload: SupportConversationUpdatePayload) => {
        set((state) => {
          const { conversation } = payload;
          const idx = state.conversations.findIndex(
            (c) => c.id === conversation.id
          );
          if (idx !== -1) {
            state.conversations[idx] = conversation;
          } else {
            state.conversations.unshift(conversation);
          }
        });
      },

      handleAgentTyping: (typing: boolean) => {
        set((state) => {
          state.agentTyping = typing;
        });
      },

      handleMessagesRead: (payload: SupportMessagesReadPayload) => {
        // Only the TEAM reading marks the user's messages "Seen by our team".
        // (The user opening their own ticket also fires this event, with
        // reader: 'user' — applying that would fake the receipt.)
        if (payload.reader !== "team") return;
        set((state) => {
          if (payload.conversation_id === state.activeConversationId) {
            for (const msg of state.messages) {
              if (msg.sender_type === "user" && !msg.read_at) {
                msg.read_at = payload.read_at;
              }
            }
          }
        });
      },
    })),
    { name: "SupportStore", enabled: process.env.NODE_ENV === "development" }
  )
);
